import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download } from "lucide-react";
import api from "../api";
import { Seo } from "../components/Seo";
import { StatusPill } from "../components/StatusPill";

const FLOW = ["Pending", "Payment Received", "Processing", "Document Found", "Verification", "Ready", "Delivered"];

export default function OrderDetail() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => { api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => {}); }, [orderId]);

  const download = async (file) => {
    try {
      const r = await api.get(`/orders/${orderId}/download/${file.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = file.name; a.click();
      URL.revokeObjectURL(url);
    } catch { alert("Download failed or document not released yet"); }
  };

  if (!order) return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-muted-foreground">Loading...</div>;

  const idx = FLOW.indexOf(order.status);

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="order-detail-page">
      <Seo title={`Order ${order.order_id}`} description="Track your DocGalaxy order." />
      <div className="bg-white border border-border rounded-xl p-6">
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <h1 className="font-display font-bold text-2xl">{order.item_name}</h1>
            <p className="font-mono text-muted-foreground" data-testid="order-detail-id">{order.order_id}</p>
            <p className="text-sm text-muted-foreground mt-1" data-testid="order-detail-sro"><span className="font-semibold">SRO:</span> {order.property_details?.sro || "—"}</p>
          </div>
          <StatusPill status={order.status} />
        </div>

        <div className="mt-6" data-testid="order-timeline">
          {FLOW.map((s, i) => (
            <div key={s} className="flex items-center gap-3 py-1.5">
              <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${i <= idx ? "bg-secondary border-secondary" : "border-border"}`}>
                {i <= idx && <span className="w-2 h-2 bg-white rounded-full" />}
              </span>
              <span className={`text-sm ${i <= idx ? "font-semibold" : "text-muted-foreground"}`}>{s}</span>
            </div>
          ))}
        </div>

        {order.deliverables?.length > 0 && (
          <div className="mt-6" data-testid="order-deliverables">
            <h3 className="font-semibold mb-2">Documents</h3>
            {order.deliverables.map((f) => (
              <button key={f.id} onClick={() => download(f)} data-testid={`download-${f.id}`}
                className="flex items-center gap-2 text-sm text-primary hover:underline">
                <Download size={14} /> {f.name}
              </button>
            ))}
          </div>
        )}
        {order.notes && <p className="mt-4 text-sm bg-muted rounded-lg p-3"><strong>Note:</strong> {order.notes}</p>}
        {order.mock_payment && <p className="mt-4 text-xs text-amber-700 bg-amber-50 rounded p-2">This order was paid in MOCK mode (Razorpay keys not configured).</p>}
      </div>
    </div>
  );
}
